// A few worked guardrails as starting points, in the person's words, each a
// document the person can read before saving (03-the-guardrails-service.md §
// the console). Each reads only the inputs an enforcement point delivers
// (01-rules-and-enforcement.md § the expression language) and concludes by
// one of the four literals.

import { inference, subject } from "@impire-io/sdk";

import { Approver, DEFAULT_TRANSFORM_TIMEOUT, selector, type Rule } from "../../wire/guard.ts";

/** ANY is the pattern token for any one workload, tool or capability. */
const ANY = "*";

/** Starter is a worked rule: what it does in a sentence, and the document. */
export interface Starter {
  key: string;
  title: string;
  sentence: string;
  rule: Rule;
}

export const STARTERS: readonly Starter[] = [
  {
    key: "confirm-deletions",
    title: "Deleting needs the person to confirm",
    sentence: "Any tool whose name begins with delete- waits until the person who asked confirms it.",
    rule: {
      id: "confirm-deletions",
      selector: selector(ANY, subject.TOKEN_TOOL, ANY),
      expression: "subject.tokens[3].startsWith('delete-') ? 'escalate' : 'allow'",
      escalation: { approver: Approver.self, ttl: "24h", "grant-ttl": "1h", context: ["args"] },
      enabled: true,
    },
  },
  {
    key: "admin-confirms-close-account",
    title: "An admin confirms closing an account",
    sentence: "A call to close-account on any workload waits until an admin confirms it.",
    rule: {
      id: "admin-confirms-close-account",
      selector: selector(ANY, "close-account"),
      expression: "'escalate'",
      escalation: {
        approver: Approver.admin,
        ttl: "72h",
        "grant-ttl": "24h",
        context: ["args", "principal.display"],
      },
      enabled: true,
    },
  },
  {
    key: "small-requests",
    title: "No request over 200 KiB",
    sentence: "A request to any model larger than 200 KiB is refused.",
    rule: {
      id: "small-requests",
      selector: selector(ANY, subject.TOKEN_INFER, ANY),
      expression: "request.size > 204800 ? 'deny' : 'allow'",
      enabled: true,
    },
  },
  {
    key: "weekdays-only",
    title: "Generation on weekdays only",
    sentence: "A chat request to any model on a Saturday or a Sunday is refused.",
    rule: {
      id: "weekdays-only",
      selector: selector(ANY, subject.TOKEN_INFER, inference.Capability.chat),
      expression: "time.weekday == 0 || time.weekday == 6 ? 'deny' : 'allow'",
      enabled: true,
    },
  },
  {
    key: "no-tools-from-apps",
    title: "Apps may not pass tools to a model",
    sentence: "A chat request from a connected app that carries tool definitions is refused.",
    rule: {
      id: "no-tools-from-apps",
      selector: selector(ANY, subject.TOKEN_INFER, inference.Capability.chat),
      expression: "principal.kind == 'app' && has(args.tools) && size(args.tools) > 0 ? 'deny' : 'allow'",
      enabled: true,
    },
  },
  {
    key: "pseudonymise-personal-data",
    title: "Personal data is pseudonymised before a model reads it",
    sentence:
      "A chat request in which personal data was found passes through the transformer pseudonymiser first.",
    rule: {
      id: "pseudonymise-personal-data",
      selector: selector(ANY, subject.TOKEN_INFER, inference.Capability.chat),
      expression: "findings.exists(f, f.kind == 'personal-data') ? 'transform' : 'allow'",
      transformer: { name: "pseudonymiser", timeout: DEFAULT_TRANSFORM_TIMEOUT },
      enabled: true,
    },
  },
];

/** BLANK is a rule to start from nothing. */
export const BLANK: Rule = {
  id: "",
  selector: selector(ANY, subject.TOKEN_TOOL, ANY),
  expression: "'allow'",
  enabled: true,
};
